import React from "react";
import "./GroceryList.css";
import { useQueryClient } from "@tanstack/react-query";
import {
  GroupedItems,
  Product,
  ProductGroup,
  QuantityUnit,
  ShoppingListItem,
  ShoppingListItemWithProduct,
  useAddToShoppingListMutation,
  useClearDoneItemsMutation,
  useCreateProductAndAddToListMutation,
  useProductMutations,
  useShoppingListItemMutations,
} from "utils/grocyApi.ts";
import {
  useProductGroupsQuery,
  useProductsQuery,
  useQuantityUnitsQuery,
  useShoppingListQuery,
  useUserSettingsQuery,
} from "hooks/useQueries.ts";
import { queryKeys } from "hooks/queryKeys.ts";
import { getShowCompletedItems, setShowCompletedItems } from "utils/storage.ts";
import { formatQuantityUnit } from "utils/quantityUnitHelpers.ts";
import FloatingUndoButton from "./FloatingUndoButton.tsx";
import AddItemModal, { AddItemData } from "./AddItemModal.tsx";

// Custom hook for fetching all grocery data
const useGroceryData = () => {
  const queryClient = useQueryClient();

  // Queries - using centralized query hooks
  const {
    data: shoppingListItems = [],
  } = useShoppingListQuery();

  const {
    data: productsData = [],
  } = useProductsQuery();

  const {
    data: productGroupsData = [],
  } = useProductGroupsQuery();

  const {
    data: quantityUnitsData = [],
  } = useQuantityUnitsQuery();

  // Create lookup maps for products, product groups, and quantity units
  const productsMap: Record<number, Product> = {};
  (productsData as Product[]).forEach((product) => {
    productsMap[product.id] = product;
  });

  const productGroupsMap: Record<number, ProductGroup> = {};
  (productGroupsData as ProductGroup[]).forEach((group) => {
    productGroupsMap[group.id] = group;
  });

  const quantityUnitsMap: Record<number, QuantityUnit> = {};
  (quantityUnitsData as QuantityUnit[]).forEach((unit) => {
    quantityUnitsMap[unit.id] = unit;
  });

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.shoppingList });
    queryClient.invalidateQueries({ queryKey: queryKeys.products });
    queryClient.invalidateQueries({ queryKey: queryKeys.productGroups });
    queryClient.invalidateQueries({ queryKey: queryKeys.quantityUnits });
  };

  return {
    shoppingListItems,
    productsMap,
    productGroupsMap,
    quantityUnitsMap,
    refreshAll,
  };
};

// Custom hook for business logic
const useGroceryLogic = (
  shoppingListItems: ShoppingListItem[],
  productsMap: Record<number, Product>,
  productGroupsMap: Record<number, ProductGroup>,
  quantityUnitsMap: Record<number, QuantityUnit>,
  showDoneItems: boolean,
) => {
  // Combine shopping list items with product, product group, and quantity unit information
  const itemsWithProducts: ShoppingListItemWithProduct[] = shoppingListItems
    .filter((item) => showDoneItems || !item.done)
    .map((item) => {
      const product = productsMap[item.product_id];
      const groupId = product?.product_group_id ?? undefined;
      return {
        ...item,
        product,
        productGroup: typeof groupId === "number"
          ? productGroupsMap[groupId]
          : undefined,
        quantityUnit: quantityUnitsMap[item.qu_id],
      };
    });

  const getGroupKey = (item: ShoppingListItemWithProduct): string => {
    // First try to group by userfields.aisle
    if (item.product?.userfields?.aisle) {
      return item.product.userfields.aisle;
    }

    // Fallback to product group name
    if (item.productGroup?.name) {
      return item.productGroup.name;
    }

    // Final fallback
    return "Other";
  };

  const formatAmount = (
    amount: number,
    quantityUnit?: QuantityUnit,
  ): string => {
    return formatQuantityUnit(amount, quantityUnit);
  };

  const getItemSortName = (item: ShoppingListItemWithProduct): string =>
    item.product?.name || `Product ID: ${item.product_id}`;

  const groupItems = (items: ShoppingListItemWithProduct[]): GroupedItems => {
    const grouped: GroupedItems = {};

    items.forEach((item) => {
      const groupKey = getGroupKey(item);
      if (!grouped[groupKey]) {
        grouped[groupKey] = [];
      }
      grouped[groupKey].push(item);
    });

    Object.values(grouped).forEach((group) => {
      group.sort((a, b) =>
        getItemSortName(a).localeCompare(getItemSortName(b), undefined, {
          numeric: true,
          sensitivity: "base",
        }) ||
        (a.note ?? "").localeCompare(b.note ?? "", undefined, {
          numeric: true,
          sensitivity: "base",
        }) ||
        a.id - b.id
      );
    });

    return grouped;
  };

  return {
    itemsWithProducts,
    getGroupKey,
    formatAmount,
    groupItems,
  };
};

// Aisle picker modal component
const AislePicker: React.FC<{
  item: ShoppingListItemWithProduct;
  editingItemId: number | null;
  onAisleSelect: (productId: number, aisle: string) => void;
  onCancelEdit: () => void;
}> = ({ item, editingItemId, onAisleSelect, onCancelEdit }) => {
  if (editingItemId !== item.id) return null;

  return (
    <div className="grocery-list__aisle-picker-overlay" onClick={onCancelEdit}>
      <div
        className="grocery-list__aisle-picker"
        onClick={(e) =>
          e.stopPropagation()}
      >
        <div className="grocery-list__aisle-picker-header">
          <h4>Select Aisle for {item.product?.name}</h4>
          <button
            type="button"
            className="grocery-list__aisle-picker-close"
            onClick={onCancelEdit}
          >
            ×
          </button>
        </div>
        <div className="grocery-list__aisle-picker-grid">
          {Array.from({ length: 12 }, (_, i) =>
            i + 1).map((aisle) => (
              <button
                type="button"
                key={aisle}
                className={`grocery-list__aisle-button ${
                  item.product?.userfields?.aisle === aisle.toString()
                    ? "grocery-list__aisle-button--selected"
                    : ""
                }`}
                onClick={() => onAisleSelect(item.product_id, aisle.toString())}
              >
                {aisle}
              </button>
            ))}
        </div>
      </div>
    </div>
  );
};

// Empty state component
const GroceryListEmpty: React.FC<{ message: string; subMessage: string }> = (
  { message, subMessage },
) => (
  <div className="grocery-list__empty">
    <p>{message}</p>
    <p>{subMessage}</p>
  </div>
);

// Grocery list item component
const GroceryListItem: React.FC<{
  item: ShoppingListItemWithProduct;
  editingItemId: number | null;
  formatAmount: (amount: number, quantityUnit?: QuantityUnit) => string;
  onEditAisle: (itemId: number) => void;
  onMarkAsDone: (itemId: number, event?: React.MouseEvent) => void;
  onMarkAsUndone: (itemId: number) => void;
  onAisleSelect: (productId: number, aisle: string) => void;
  onCancelEdit: () => void;
}> = ({
  item,
  editingItemId,
  formatAmount,
  onEditAisle,
  onMarkAsDone,
  onMarkAsUndone,
  onAisleSelect,
  onCancelEdit,
}) => (
  <div
    key={item.id}
    className={`grocery-list__item ${
      item.done ? "grocery-list__item--done" : ""
    }`}
  >
    <div className="grocery-list__item-content">
      {item.amount > 0
        ? (
          <span className="grocery-list__item-amount">
            {formatAmount(item.amount, item.quantityUnit)}
          </span>
        )
        : null}
      <div className="grocery-list__item-name">
        {item.product?.name || `Product ID: ${item.product_id}`}
      </div>
      {item.note
        ? (
          <div className="grocery-list__item-details">
            <span className="grocery-list__item-note">
              {item.note}
            </span>
          </div>
        )
        : null}
    </div>
    <button
      type="button"
      onClick={() => onEditAisle(item.id)}
      className="grocery-list__aisle-button-small"
      title="Set aisle"
    >
      {item.product?.userfields?.aisle || "?"}
    </button>
    {!item.done
      ? (
        <button
          type="button"
          onClick={(event) => onMarkAsDone(item.id, event)}
          className="grocery-list__done-button"
          title="Mark as done"
        >
          ✓
        </button>
      )
      : (
        <button
          type="button"
          onClick={() => onMarkAsUndone(item.id)}
          className="grocery-list__undo-button"
          title="Mark as undone"
        >
          ↺
        </button>
      )}
    <AislePicker
      item={item}
      editingItemId={editingItemId}
      onAisleSelect={onAisleSelect}
      onCancelEdit={onCancelEdit}
    />
  </div>
);

// Grocery list group component
const GroceryListGroup: React.FC<{
  groupName: string;
  items: ShoppingListItemWithProduct[];
  editingItemId: number | null;
  formatAmount: (amount: number, quantityUnit?: QuantityUnit) => string;
  onEditAisle: (itemId: number) => void;
  onMarkAsDone: (itemId: number, event?: React.MouseEvent) => void;
  onMarkAsUndone: (itemId: number) => void;
  onAisleSelect: (productId: number, aisle: string) => void;
  onCancelEdit: () => void;
}> = ({
  groupName,
  items,
  editingItemId,
  formatAmount,
  onEditAisle,
  onMarkAsDone,
  onMarkAsUndone,
  onAisleSelect,
  onCancelEdit,
}) => (
  <div key={groupName} className="grocery-list__group">
    <h3 className="grocery-list__group-title">
      {!isNaN(Number(groupName)) ? `Aisle ${groupName}` : groupName}
    </h3>
    <div className="grocery-list__items">
      {items.map((item) => (
        <GroceryListItem
          key={item.id}
          item={item}
          editingItemId={editingItemId}
          formatAmount={formatAmount}
          onEditAisle={onEditAisle}
          onMarkAsDone={onMarkAsDone}
          onMarkAsUndone={onMarkAsUndone}
          onAisleSelect={onAisleSelect}
          onCancelEdit={onCancelEdit}
        />
      ))}
    </div>
  </div>
);

const GroceryList: React.FC = () => {
  const [showDoneItems, setShowDoneItems] = React.useState(() =>
    getShowCompletedItems()
  );
  const [editingItemId, setEditingItemId] = React.useState<number | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = React.useState(false);
  const [recentlyCompletedItem, setRecentlyCompletedItem] = React.useState<
    {
      itemId: number;
      position: { x: number; y: number };
      timestamp: number;
    } | null
  >(null);

  // Queries
  const {
    shoppingListItems,
    productsMap,
    productGroupsMap,
    quantityUnitsMap,
    refreshAll,
  } = useGroceryData();
  const { data: userSettings } = useUserSettingsQuery();
  const presetQuantityUnitId = Number(userSettings?.product_presets_qu_id) ||
    undefined;

  // Use predefined mutations
  const { markAsDoneMutation, markAsUndoneMutation } =
    useShoppingListItemMutations();
  const { updateAisleMutation } = useProductMutations(() =>
    setEditingItemId(null)
  );
  const clearDoneItemsMutation = useClearDoneItemsMutation();
  const addToShoppingListMutation = useAddToShoppingListMutation();
  const createProductAndAddToListMutation =
    useCreateProductAndAddToListMutation();

  // Use the business logic hook
  const { itemsWithProducts, formatAmount, groupItems } = useGroceryLogic(
    shoppingListItems,
    productsMap,
    productGroupsMap,
    quantityUnitsMap,
    showDoneItems,
  );

  const handleMarkAsDone = (itemId: number, event?: React.MouseEvent) => {
    markAsDoneMutation.mutate(itemId);

    // If completed items are hidden, show floating undo button
    if (!showDoneItems && event) {
      const rect = event.currentTarget.getBoundingClientRect();
      setRecentlyCompletedItem({
        itemId,
        position: {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        },
        timestamp: Date.now(),
      });
    }
  };

  const handleMarkAsUndone = (itemId: number) => {
    markAsUndoneMutation.mutate(itemId);
  };

  const handleRefresh = () => {
    refreshAll();
  };

  const handleEditAisle = (itemId: number) => {
    setEditingItemId(itemId);
  };

  const handleAisleSelect = (productId: number, aisle: string) => {
    updateAisleMutation.mutate({ productId, aisle });
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
  };

  const handleClearDoneItems = () => {
    const confirmed = globalThis.confirm(
      "Are you sure you want to clear all completed items from your shopping list? This action cannot be undone.",
    );

    if (confirmed) {
      clearDoneItemsMutation.mutate();
    }
  };

  const handleAddItem = async (data: AddItemData) => {
    try {
      if (data.isNewProduct && data.newProductData) {
        // Create new product and add to shopping list
        await createProductAndAddToListMutation.mutateAsync({
          productData: {
            name: data.newProductData.name,
            location_id: data.newProductData.locationId,
            product_group_id: data.newProductData.productGroupId,
            parent_product_id: data.newProductData.parentProductId,
            qu_id_purchase: presetQuantityUnitId,
            qu_id_stock: presetQuantityUnitId,
          },
          amount: data.amount,
        });
      } else if (data.productId) {
        // Add existing product to shopping list
        const product = productsMap[data.productId];
        await addToShoppingListMutation.mutateAsync({
          product_id: data.productId,
          product_amount: data.amount,
          qu_id: product?.qu_id_purchase,
        });
      }
      setIsAddModalOpen(false);
    } catch (error) {
      console.error("Error adding item to shopping list:", error);
      // TODO: Show error message to user
    }
  };

  const groupedItems = groupItems(itemsWithProducts);

  // Sort groups with numeric sorting for numbers
  const sortedGroups = Object.keys(groupedItems).sort((a, b) => {
    // Check if both are numbers
    const aNum = parseFloat(a);
    const bNum = parseFloat(b);

    if (!isNaN(aNum) && !isNaN(bNum)) {
      // Both are numbers, sort numerically
      return aNum - bNum;
    } else if (!isNaN(aNum)) {
      // Only a is a number, put numbers first
      return -1;
    } else if (!isNaN(bNum)) {
      // Only b is a number, put numbers first
      return 1;
    } else {
      // Both are strings, sort alphabetically
      return a.localeCompare(b);
    }
  });

  const isAnyMutationPending = markAsDoneMutation.isPending ||
    markAsUndoneMutation.isPending || clearDoneItemsMutation.isPending ||
    addToShoppingListMutation.isPending ||
    createProductAndAddToListMutation.isPending;

  return (
    <div className="grocery-list">
      {isAnyMutationPending && (
        <div className="grocery-list__global-loading">
          <div className="grocery-list__loading-dot"></div>
        </div>
      )}
      <div className="grocery-list__content">
        {shoppingListItems.length === 0
          ? (
            <GroceryListEmpty
              message="Your grocery list is empty."
              subMessage="Add items by scanning barcodes or manually adding them to your Grocy instance."
            />
          )
          : itemsWithProducts.length === 0
          ? (
            <GroceryListEmpty
              message="All items are completed!"
              subMessage="Uncheck 'Show completed items' to see what you've accomplished."
            />
          )
          : (
            <div className="grocery-list__groups">
              {sortedGroups.map((groupName) => (
                <GroceryListGroup
                  key={groupName}
                  groupName={groupName}
                  items={groupedItems[groupName]}
                  editingItemId={editingItemId}
                  formatAmount={formatAmount}
                  onEditAisle={handleEditAisle}
                  onMarkAsDone={handleMarkAsDone}
                  onMarkAsUndone={handleMarkAsUndone}
                  onAisleSelect={handleAisleSelect}
                  onCancelEdit={handleCancelEdit}
                />
              ))}
            </div>
          )}
        <div className="grocery-list__actions">
          <div className="grocery-list__actions-row">
            <label className="grocery-list__toggle">
              <input
                type="checkbox"
                checked={showDoneItems}
                onChange={(e) => {
                  const newValue = e.target.checked;
                  setShowDoneItems(newValue);
                  setShowCompletedItems(newValue);
                }}
              />
              <span className="grocery-list__toggle-label">
                Show completed items
              </span>
            </label>
            <button
              type="button"
              onClick={() => setIsAddModalOpen(true)}
              className="grocery-list__add-button"
            >
              + Add Item
            </button>
          </div>
          <div className="grocery-list__buttons-group">
            <button
              type="button"
              onClick={handleRefresh}
              className="grocery-list__refresh-button"
            >
              Refresh List
            </button>
            <button
              type="button"
              onClick={handleClearDoneItems}
              className="grocery-list__clear-button"
              disabled={clearDoneItemsMutation.isPending}
            >
              {clearDoneItemsMutation.isPending
                ? "Clearing..."
                : "Clear Completed"}
            </button>
          </div>
        </div>
      </div>
      {recentlyCompletedItem && (
        <FloatingUndoButton
          position={recentlyCompletedItem.position}
          onUndo={() => handleMarkAsUndone(recentlyCompletedItem.itemId)}
          onDismiss={() => setRecentlyCompletedItem(null)}
        />
      )}

      <AddItemModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAddItem={handleAddItem}
      />
    </div>
  );
};

export default GroceryList;
